/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { ICartLineCommentData } from './cart-line-comment.data';
import { ICartLineCommentProps } from './cart-line-comment.props.autogenerated';
import { Button, IModuleProps, INodeProps, Modal, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';

export interface ICartLineCommentViewProps extends ICartLineCommentProps<ICartLineCommentData> {
    staticDisplay: INodeProps;
    cartLineCommentModuleProps: IModuleProps;
    modal: React.ReactNode;
}

interface ICartLineCommentExtendedProps extends ICartLineCommentProps<ICartLineCommentData> {
    comment: string;
    cartLineId: string;
    cartProductTitle: string;
}

interface ICartLineCommentState {
    comment: string;
    isCommentAreaActive: boolean;
}
/**
 *
 * CartLineComment component
 * @extends {React.PureComponent<ICartLineCommentProps<ICartLineCommentData>>}
 */
class CartLineComment extends React.PureComponent<ICartLineCommentExtendedProps, ICartLineCommentState> {
    private _className = 'gar-cart-comment';
    constructor(props: ICartLineCommentExtendedProps) {
        super(props);
        this.state = {
            comment: props.comment,
            isCommentAreaActive: false
        };
    }

    /**
     * Saves cart comment
     * Method must call updateCartLineQuantity on the cart state because
     * updateCart does not update the cart line comment. Calling the other method with an additional
     * parameter for the comment successfully updates the comment
     */
    private _updateCartLineComment = async (): Promise<void> => {
        const cartState = this.props.data.cart.result;
        if (!cartState) {
            return;
        }
        const dummyCart = { ...cartState.cart };
        const cartLineToUpdate = dummyCart.CartLines?.find(cl => cl.LineId === this.props.cartLineId);
        if (cartLineToUpdate) {
            cartLineToUpdate.Comment = this.state.comment;
        }
        await cartState.updateCartLineQuantity({
            cartLineId: this.props.cartLineId,
            newQuantity: cartLineToUpdate?.Quantity!,
            additionalProperties: { Comment: this.state.comment }
        });

        this.setState({ isCommentAreaActive: false });
    };

    /**
     * Updates the comment in state
     * @param event new comment from event
     */
    private _updateCommentState = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        this.setState({ comment: event.target.value });
    };
    /**
     * Resets Cart Comment
     */
    private _resetCommentToProps = (): void => {
        this.setState({ comment: this.props.comment, isCommentAreaActive: false });
    };

    /**
     * Toggles the isCommentAreaActive state property
     */
    private _toggleCommentAreaActive = () =>
        this.setState(prevState => {
            return {
                isCommentAreaActive: !prevState.isCommentAreaActive
            };
        });

    /**
     * Renders the static comment display for when the isCommentAreaActive state param is false
     * @return React.ReactNode
     */
    private _renderStaticCommentDisplay = (): React.ReactNode => {
        const {
            resources: { commentLinkText, editLinkText }
        } = this.props;
        if (!this.props.comment) {
            return this._renderCommentLink(commentLinkText);
        } else {
            return (
                <React.Fragment>
                    <p className={`${this._className}__comment-preview`}>{this._getCommentPreview()}</p>
                    {this._renderCommentLink(editLinkText)}
                </React.Fragment>
            );
        }
    };

    private _getCommentPreview = (): string => {
        const { comment } = this.props;
        if (comment.length > 50) {
            return `${comment.substring(0, 50)}...`;
        } else {
            return comment;
        }
    };

    /**
     * Renders a comment link which toggles the active area state
     * @param linkText text for the link
     */
    private _renderCommentLink = (linkText: string): React.ReactNode => (
        <a href={'#'} onClick={this._toggleCommentAreaActive} className={`${this._className}__link`}>
            {linkText}
        </a>
    );

    /**
     * Renders a button to save comments to the cartlines
     * @return React.ReactNode
     */
    private _renderSaveButton = (): React.ReactNode => {
        const {
            resources: { saveButtonText }
        } = this.props;
        return (
            <Button onClick={this._updateCartLineComment} className={`${this._className}__button ${this._className}__button--save`}>
                {saveButtonText}
            </Button>
        );
    };

    /**
     * Renders a button to cancel the comment entry and set the state to default
     * @return React.ReactNode
     */
    private _renderCancelButton = (): React.ReactNode => {
        const {
            resources: { cancelButtonText }
        } = this.props;
        return (
            <Button onClick={this._resetCommentToProps} className={`${this._className}__button ${this._className}__button--cancel`}>
                {cancelButtonText}
            </Button>
        );
    };

    /**
     * Renders the text area for inputting comments
     */
    private _renderTextArea = (): React.ReactNode => {
        const {
            resources: { textAreaLabel }
        } = this.props;
        const { comment } = this.state;
        return (
            <div className={`${this._className}__textarea-wrapper`}>
                <label htmlFor={this._getFieldId()} className={`${this._className}__textarea-label`}>
                    {textAreaLabel}
                </label>
                <textarea
                    id={this._getFieldId()}
                    value={comment}
                    onChange={this._updateCommentState}
                    className={`${this._className}__textarea`}
                />
            </div>
        );
    };
    /**
     * Renders the button area
     */
    private _renderButtonArea = (): React.ReactNode => (
        <div className={`${this._className}__button-area`}>
            {this._renderSaveButton()}
            {this._renderCancelButton()}
        </div>
    );

    private _renderModal = () => {
        return (
            <Modal isOpen={this.state.isCommentAreaActive} toggle={this._toggleCommentAreaActive}>
                <ModalHeader className={`${this._className}__modal-header`}>{this._getModalTitle()}</ModalHeader>
                {this._renderTextArea()}
                <ModalFooter>{this._renderButtonArea()}</ModalFooter>
            </Modal>
        );
    };

    /**
     * Gets ID for the text field
     */
    private _getFieldId = (): string => `${this.props.cartLineId}__commentInput`;

    /**
     * Gets the title for the comment modal
     */
    private _getModalTitle = (): string => {
        const {
            resources: { commentModalTitle }
        } = this.props;

        return commentModalTitle.replace('{0}', this.props.cartProductTitle);
    };

    public render(): JSX.Element | null {
        const {
            config: { className }
        } = this.props;
        const viewProps: ICartLineCommentViewProps = {
            ...this.props,
            staticDisplay: {
                tag: 'div',
                className: `${this._className}__static-display`,
                children: this._renderStaticCommentDisplay()
            },
            cartLineCommentModuleProps: {
                className: classnames(className, this._className),
                tag: 'div',
                moduleProps: { ...this.props }
            },
            modal: this._renderModal()
        };
        return this.props.renderView(viewProps);
    }
}

export default CartLineComment;
